import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Droplet, AlertTriangle, Loader2 } from "lucide-react";

export default function MoistureEquipment() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/management/moisture/equipment-report");
      setReport(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="moisture-equipment-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-zinc-900 flex items-center gap-2">
          <Droplet className="w-6 h-6 text-red-600" /> Moisture Equipment Report
        </h1>
        <p className="text-sm text-zinc-500">AI-recommended vs deployed equipment across all active jobs.</p>
      </div>

      <Card className="border border-zinc-200">
        <CardContent className="p-4">
          <p className="text-xs font-bold uppercase text-zinc-500 mb-3">By Equipment Type</p>
          <div className="space-y-2">
            {(report?.by_type || []).map(t => {
              const var_ = (t.total_deployed || 0) - (t.total_recommended || 0);
              const tone = var_ === 0 ? "text-emerald-600" : Math.abs(var_) > 2 ? "text-red-600" : "text-amber-600";
              return (
                <div key={t.equipment_type} className="flex items-center gap-3 border-b border-zinc-100 py-2"
                     data-testid={`eq-row-${t.equipment_type?.replace(/\W/g,'')}`}>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-zinc-900">{t.equipment_type}</p>
                    <p className="text-[10px] text-zinc-500">{t.job_count} job(s)</p>
                  </div>
                  <div className="grid grid-cols-3 text-center gap-3 text-xs">
                    <div><p className="text-[10px] text-zinc-400">REC</p><p className="font-bold text-zinc-900">{t.total_recommended}</p></div>
                    <div><p className="text-[10px] text-zinc-400">DEP</p><p className="font-bold text-zinc-900">{t.total_deployed}</p></div>
                    <div><p className="text-[10px] text-zinc-400">VAR</p><p className={`font-bold ${tone}`}>{var_ > 0 ? `+${var_}` : var_}</p></div>
                  </div>
                </div>
              );
            })}
            {(report?.by_type || []).length === 0 && (
              <p className="text-xs text-zinc-400 italic text-center py-6">No equipment tracked yet.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {report?.flagged_jobs?.length > 0 && (
        <Card className="border border-red-200">
          <CardContent className="p-4">
            <p className="text-xs font-bold uppercase text-red-600 mb-3 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Variance Flags (≥25%)
            </p>
            <div className="space-y-2">
              {report.flagged_jobs.map(f => (
                <div key={f.request_id} className="flex items-center justify-between border border-red-100 rounded p-2 bg-red-50">
                  <p className="text-xs font-mono text-zinc-700">{f.request_id.slice(0, 12)}…</p>
                  <Badge className="bg-red-100 text-red-700 border-red-300 border">
                    {f.variance_pct > 0 ? `+${f.variance_pct}%` : `${f.variance_pct}%`} variance · {f.deployed}/{f.recommended}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
